import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/lib/api'
import type { AuthToken, CreateTokenRequest } from '@/types/models'

interface OidcUserInfo {
  authenticated: boolean
  username?: string
  email?: string
  groups?: string[]
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const isLoading = ref(true)
  const bearerToken = ref<string | null>(null)
  const user = ref<{ email?: string; username?: string; role?: string; groups?: string[] } | null>(null)
  const tokens = ref<AuthToken[]>([])

  // Computed
  const hasValidToken = computed(() => !!bearerToken.value)
  const currentUser = computed(() => user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  // Actions
  async function initialize() {
    isLoading.value = true
    try {
      // Check for a persisted Bearer token (CLI / programmatic access).
      const token = localStorage.getItem('api_token') || import.meta.env.VITE_API_TOKEN
      if (token) {
        bearerToken.value = token
        apiClient.setBearerToken(token)
        isAuthenticated.value = true
        // With a Bearer token, /api/v1/me is the source of truth.
        await fetchMe()
        return
      }

      // Otherwise use OIDC session cookie via /oidc/userinfo.
      await checkOidcSession()
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Call the OIDC userinfo endpoint to check if the browser has a valid
   * session cookie. This endpoint is public and never triggers a redirect.
   */
  async function checkOidcSession() {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/oidc/userinfo`, {
        credentials: 'include',
      })

      if (!response.ok) {
        isAuthenticated.value = false
        user.value = null
        return
      }

      const data = (await response.json()) as OidcUserInfo
      if (!data.authenticated) {
        isAuthenticated.value = false
        user.value = null
        return
      }

      isAuthenticated.value = true
      user.value = {
        username: data.username,
        email: data.email,
        groups: data.groups ?? [],
      }

      // Hit /api/v1/me so the server auto-provisions the user record and
      // returns the authoritative role. Failures are non-fatal — the UI will
      // just fall back to whatever userinfo returned.
      try {
        await fetchMe()
      } catch (err) {
        console.warn('Failed to fetch /api/v1/me after OIDC session check:', err)
      }
    } catch (err) {
      console.error('OIDC session check failed:', err)
      isAuthenticated.value = false
      user.value = null
    }
  }

  async function fetchMe() {
    const response = await apiClient.get<{
      id: string
      username: string
      email: string
      role: string
    }>('/api/v1/me')

    user.value = {
      username: response.username,
      email: response.email,
      role: response.role,
      groups: user.value?.groups,
    }
    isAuthenticated.value = true
  }

  function setToken(token: string) {
    bearerToken.value = token
    apiClient.setBearerToken(token)
    isAuthenticated.value = true
  }

  function clearAuth() {
    bearerToken.value = null
    user.value = null
    isAuthenticated.value = false
    tokens.value = []
    apiClient.clearBearerToken()
  }

  /**
   * Redirect the browser to the OIDC login endpoint.
   */
  function login() {
    const apiUrl = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiUrl}/oidc/login`
  }

  /**
   * Clear local state and redirect the browser to the OIDC logout endpoint,
   * which will destroy the session cookie and bounce through the IdP logout.
   */
  function logout() {
    clearAuth()
    const apiUrl = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiUrl}/oidc/logout`
  }

  async function fetchTokens() {
    try {
      const response = await apiClient.get<any>('/api/v1/auth/tokens')
      const tokenList = Array.isArray(response) ? response : []
      tokens.value = tokenList.map((token: any) => ({
        id: token.id,
        name: token.name,
        expiresAt: token.expires_at,
        lastUsedAt: token.last_used_at,
      }))
    } catch (error) {
      console.error('Failed to fetch tokens:', error)
      throw error
    }
  }

  async function createToken(data: CreateTokenRequest): Promise<string> {
    try {
      const response = await apiClient.post<any>('/api/v1/auth/tokens', {
        name: data.name,
        expires_in_days: data.expiresInDays || 30,
      })

      tokens.value.unshift({
        id: response.id,
        name: response.name,
        expiresAt: response.expires_at,
        lastUsedAt: response.last_used_at,
      })

      return response.token
    } catch (error) {
      console.error('Failed to create token:', error)
      throw error
    }
  }

  async function revokeToken(id: string) {
    try {
      await apiClient.delete(`/api/v1/auth/tokens/${id}`)
      tokens.value = tokens.value.filter(t => t.id !== id)
    } catch (error) {
      console.error('Failed to revoke token:', error)
      throw error
    }
  }

  return {
    // State
    isAuthenticated,
    isLoading,
    bearerToken,
    user,
    tokens,
    // Computed
    hasValidToken,
    currentUser,
    isAdmin,
    // Actions
    initialize,
    checkOidcSession,
    setToken,
    clearAuth,
    login,
    logout,
    fetchTokens,
    createToken,
    revokeToken,
  }
})
